import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lightning, Lock, CreditCard } from '@phosphor-icons/react';
import { useCompany } from '../context/CompanyContext';

// Top-of-app banner: surfaces a "Finish your payment" CTA whenever the
// current company is in `pending_payment` (e.g., they signed up but
// dropped off the Stripe Checkout, or their paid period lapsed).
// Trials no longer exist post-iter135, so the banner only renders for
// the pending_payment state.

export default function TrialBanner() {
  const { company } = useCompany();
  if (!company) return null;
  if (company.status !== 'pending_payment') return null;

  return (
    <div
      data-testid="pending-payment-banner"
      data-state="pending"
      className="w-full text-xs font-bold uppercase tracking-widest px-4 py-2 flex items-center justify-center gap-3 flex-wrap bg-rose-700 text-white"
    >
      <Lock size={14} weight="fill" />
      <span data-testid="pending-payment-banner-text">
        Payment required · Add a card to activate {company.plan_name}
      </span>
      <Link
        to="/billing"
        data-testid="pending-payment-banner-upgrade"
        className="px-3 py-1 bg-card text-foreground hover:bg-muted border border-slate-900 transition inline-flex items-center gap-1"
      >
        <CreditCard size={11} weight="fill" /> Add payment
      </Link>
      <Link
        to="/billing?cycle=annual"
        data-testid="pending-payment-banner-annual-hint"
        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-800 transition inline-flex items-center gap-1"
      >
        <Lightning size={11} weight="fill" /> Save 17% · Annual
      </Link>
    </div>
  );
}

// Soft-block: tenants in `pending_payment` can only access /billing,
// /settings/billing, /pricing, /legal, /logout. Everything else renders
// the paywall card so they're nudged to complete checkout.
const ALLOWED_PATH_PREFIXES = [
  '/billing',
  '/settings/billing',
  '/pricing',
  '/legal',
  '/logout',
];

export function TrialExpiredGate({ children }) {
  const { company, loading } = useCompany();
  const location = useLocation();
  if (loading || !company) return children;
  // Only block tenants that haven't completed their first payment yet.
  if (company.status !== 'pending_payment') return children;
  const path = location.pathname || '/';
  if (ALLOWED_PATH_PREFIXES.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p))) {
    return children;
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="pending-payment-paywall">
      <div className="max-w-lg text-center bg-card border-2 border-rose-600 p-8 shadow-lg">
        <div className="w-14 h-14 bg-rose-600 flex items-center justify-center mx-auto mb-4">
          <Lock size={26} weight="fill" className="text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Add a payment method to continue</h2>
        <p className="text-sm text-muted-foreground mb-1">
          Your account is set up — just one card away from going live.
        </p>
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-4">
          Plan: {company.plan_name}
        </p>
        <div className="bg-emerald-50 border border-emerald-300 px-3 py-2 mb-6 inline-block" data-testid="pending-payment-annual-callout">
          <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-800">
            <Lightning size={11} weight="fill" className="inline mr-1" />
            Annual saves 17% · 2 months free
          </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/billing?cycle=annual"
            data-testid="pending-payment-annual-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm uppercase tracking-widest transition"
          >
            <Lightning size={16} weight="fill" />
            Pay annual · save 17%
          </Link>
          <Link
            to="/billing"
            data-testid="pending-payment-upgrade-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm uppercase tracking-widest transition"
          >
            <CreditCard size={14} weight="fill" /> Pay monthly
          </Link>
          <Link
            to="/pricing"
            data-testid="pending-payment-pricing-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-foreground text-foreground hover:bg-muted font-bold text-sm uppercase tracking-widest transition"
          >
            Compare tiers
          </Link>
        </div>
      </div>
    </div>
  );
}
